import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Text, TogglerCheckbox } from '@factorialco/deprecated-design-system'
import { UpsellingButton as F0UpsellingButton } from '@factorialco/f0-react'
import { AvailableModules } from 'generated/featureDefinitions'
import {
  GetModuleAvailabilitiesDocument,
  TrainingsDevLmsFeatureFlagDocument,
} from 'generated/resources/graphql'

import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'
import { isModuleAvailabilityEnabled } from 'modules/core/lib/isModuleAvailabilityEnabled'

interface CompletionConfigurationData {
  attendancePercentage?: number
  moduleCompletedPercentage?: number
  knowledgeTestRequired: boolean
}

interface CompletionConfigurationFieldsProps {
  data: CompletionConfigurationData
  onDataChanged: (data: CompletionConfigurationData) => void
  readonly?: boolean
}

export const CompletionConfigurationFields = ({
  data,
  onDataChanged,
  readonly = false,
}: CompletionConfigurationFieldsProps) => {
  const isAttendanceEnabled = data.attendancePercentage !== undefined
  const isModuleCompletionEnabled = data.moduleCompletedPercentage !== undefined
  const [isFocused, setIsFocused] = useState(false)

  const { company } = useCurrentSession()
  const { data: featureFlagData } = useQuery(TrainingsDevLmsFeatureFlagDocument, {
    variables: { companyId: company.id },
  })

  const hasLMSDevFeatureFlag = Boolean(
    featureFlagData?.features?.featuresConnection?.nodes?.find(
      (n) => n.name === 'dev_training_lms' && n.enabled === true
    )
  )
  const { data: featureData } = useQuery(GetModuleAvailabilitiesDocument, {
    fetchPolicy: 'cache-first',
    variables: {
      ids: [AvailableModules.lms],
      companyId: company.id,
    },
  })

  const moduleAvailabilities = featureData?.companies.moduleAvailabilities ?? []
  const hasLMSModule = isModuleAvailabilityEnabled(moduleAvailabilities, AvailableModules.lms)

  const hasLMSEntitlement = hasLMSModule || hasLMSDevFeatureFlag

  const navigate = useNavigate()
  const { navigation } = useNavigation()

  return (
    <Box gap="s16">
      {/* Module Completed Percentage */}
      <Box
        gap="s12"
        padding="s16"
        border={{ all: { width: 's1', color: 'grey300', style: 'solid' } }}
        borderRadius={{ all: 'abs016' }}
      >
        <Box flexDirection="row" alignItems="flexStart" justifyContent="spaceBetween">
          <Box gap="s4" flexDirection="column">
            <Text.Normal
              size="200"
              weight="medium"
              color={hasLMSEntitlement ? 'grey1000' : 'grey600'}
            >
              {i18n.t(
                'trainings.trainings_revamp.new_training.wizard.completion_configuration.module_completed_percentage'
              )}
            </Text.Normal>
            <Text.Normal
              size="200"
              weight="normal"
              color={hasLMSEntitlement ? 'grey800' : 'grey600'}
            >
              {i18n.t(
                'trainings.trainings_revamp.new_training.wizard.completion_configuration.module_completed_percentage_helper'
              )}
            </Text.Normal>
          </Box>
          <TogglerCheckbox
            value={hasLMSEntitlement ? isModuleCompletionEnabled : false}
            onChange={
              hasLMSEntitlement
                ? (checked: boolean) =>
                    onDataChanged({
                      ...data,
                      moduleCompletedPercentage: checked ? 100 : undefined,
                    })
                : () => {}
            }
            disabled={!hasLMSEntitlement || readonly}
          />
        </Box>
        {!hasLMSEntitlement && (
          <Box paddingY="s4" width="min-content">
            <F0UpsellingButton
              variant="outlinePromote"
              label={i18n.t(
                'trainings.trainings_revamp.new_training.wizard.completion_configuration.learn_more_lms'
              )}
              errorMessage={{
                title: i18n.t('upselling_kit.upselling_button.error_message.title'),
                description: i18n.t('upselling_kit.upselling_button.error_message.description'),
              }}
              successMessage={{
                title: i18n.t('upselling_kit.upselling_button.success_message.title_2'),
                description: i18n.t('upselling_kit.upselling_button.success_message.description'),
              }}
              loadingState={{
                label: i18n.t('upselling_kit.upselling_button.loading_state.label'),
              }}
              nextSteps={{
                title: '',
                items: [],
              }}
              closeLabel={i18n.t('upselling_kit.upselling_button.close_label')}
              onRequest={() =>
                navigate(
                  navigation.discover.productUpdates.item.fullPath({
                    slug: 'transform-employee-learning-with-factorial-s-lms',
                  })
                )
              }
            />
          </Box>
        )}
      </Box>

      {/* Attendance Percentage */}
      <Box
        gap="s12"
        padding="s16"
        border={{ all: { width: 's1', color: 'grey300', style: 'solid' } }}
        borderRadius={{ all: 'abs016' }}
      >
        <Box flexDirection="row" alignItems="flexStart" justifyContent="spaceBetween">
          <Box gap="s4" flexDirection="column">
            <Text.Normal size="200" weight="medium" color="grey1000">
              {i18n.t(
                'trainings.trainings_revamp.new_training.wizard.completion_configuration.attendance_percentage'
              )}
            </Text.Normal>
            <Text.Normal size="200" weight="normal" color="grey800">
              {i18n.t(
                'trainings.trainings_revamp.new_training.wizard.completion_configuration.attendance_percentage_field_description'
              )}
            </Text.Normal>
          </Box>
          <TogglerCheckbox
            value={isAttendanceEnabled}
            onChange={(checked: boolean) =>
              onDataChanged({
                ...data,
                attendancePercentage: checked ? 100 : undefined,
              })
            }
            disabled={readonly}
          />
        </Box>
        {isAttendanceEnabled && (
          <Box gap="s4">
            <Text.Normal size="200" weight="medium" color="grey1000">
              {i18n.t(
                'trainings.trainings_revamp.new_training.wizard.completion_configuration.attendance_percentage_field_title'
              )}
            </Text.Normal>
            <Text.Normal size="200" weight="normal" color="grey800">
              {i18n.t(
                'trainings.trainings_revamp.new_training.wizard.completion_configuration.attendance_percentage_helper'
              )}
            </Text.Normal>
            <TextField
              type={isFocused ? 'number' : 'text'}
              value={
                isFocused
                  ? String(data.attendancePercentage ?? '')
                  : data.attendancePercentage !== undefined
                  ? `${data.attendancePercentage}%`
                  : ''
              }
              onChange={(value) => {
                const numValue = value.replace('%', '').trim()
                const parsed = parseInt(numValue, 10)
                if (!isNaN(parsed) && parsed >= 0 && parsed <= 100) {
                  onDataChanged({ ...data, attendancePercentage: parsed })
                } else if (numValue === '') {
                  onDataChanged({ ...data, attendancePercentage: 0 })
                }
              }}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              readonly={readonly}
            />
          </Box>
        )}
      </Box>

      {/* Knowledge Test Required */}
      <Box
        gap="s12"
        padding="s16"
        border={{ all: { width: 's1', color: 'grey300', style: 'solid' } }}
        borderRadius={{ all: 'abs016' }}
      >
        <Box flexDirection="row" alignItems="flexStart" justifyContent="spaceBetween">
          <Box gap="s4" flexDirection="column">
            <Text.Normal size="200" weight="medium" color="grey1000">
              {i18n.t(
                'trainings.trainings_revamp.new_training.wizard.completion_configuration.knowledge_test_required'
              )}
            </Text.Normal>
            <Text.Normal size="200" weight="normal" color="grey800">
              {i18n.t(
                'trainings.trainings_revamp.new_training.wizard.completion_configuration.knowledge_test_required_helper'
              )}
            </Text.Normal>
          </Box>
          <TogglerCheckbox
            value={data.knowledgeTestRequired}
            onChange={(checked: boolean) =>
              onDataChanged({ ...data, knowledgeTestRequired: checked })
            }
            disabled={readonly}
          />
        </Box>
      </Box>
    </Box>
  )
}
